import React from 'react';
import { NavLink } from 'react-router-dom';
import { useRBAC } from '../auth/RBACProvider';

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (page: string) => void;
  currentPage: string;
}

const pagePathById: Record<string, string> = {
  dashboard: '/dashboard',
  'email-campaigns': '/email-campaigns',
  'linkedin-campaigns': '/linkedin-campaigns',
  'google-analytics': '/google-analytics',
  'google-ads': '/google-ads',
  'meta-campaigns': '/meta-campaigns',
  leads: '/leads',
  prospects: '/prospects',
  'client-config': '/client-config',
  'user-management': '/user-management',
  'account-management': '/account-management',
  'system-monitoring': '/system-monitoring',
};

const Sidebar: React.FC<MenuProps> = ({ isOpen, onClose, onNavigate, currentPage }) => {
  const { isAdmin, hasPageAccess } = useRBAC();
  
  const baseMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', active: true },
    { id: 'email-campaigns', label: 'Email Campaigns', icon: '📧', active: true },
    { id: 'linkedin-campaigns', label: 'LinkedIn Campaigns', icon: '💼', active: true },
    { id: 'google-analytics', label: 'Web Campaigns', icon: '🌐', active: true },
    { id: 'google-ads', label: 'Google Ads Campaigns', icon: '🎯', active: true },
    { id: 'meta-campaigns', label: 'Meta Campaigns', icon: '📱', active: true },
    { id: 'leads', label: 'Leads', icon: '📝', active: true },
    { id: 'prospects', label: 'Prospects', icon: '👥', active: true },
    { id: 'client-config', label: 'Client Configuration', icon: '⚙️', active: true },
  ];

  const adminMenuItems = [
    { id: 'user-management', label: 'User Management', icon: '👤', active: true },
    { id: 'account-management', label: 'Account Management', icon: '🔧', active: true },
    { id: 'system-monitoring', label: 'System Monitoring', icon: '🖥️', active: true },
  ];

  // Filter menu items based on page access
  const filteredBaseItems = baseMenuItems.filter(item => hasPageAccess(item.id));
  const filteredAdminItems = isAdmin() ? adminMenuItems.filter(item => hasPageAccess(item.id)) : [];
  const menuItems = [...filteredBaseItems, ...filteredAdminItems];

  const handleItemClick = (itemId: string, active: boolean) => {
    if (active) {
      onNavigate(itemId);
      onClose();
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`sidebar ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: isOpen ? 0 : '-300px',
          width: '300px',
          height: '100vh',
          backgroundColor: '#1F2035',
          transition: 'left 0.3s ease',
          zIndex: 1000,
          padding: '20px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '30px',
          borderBottom: '1px solid #333',
          paddingBottom: '15px'
        }}>
          <h2 style={{ color: 'white', margin: 0, fontSize: '18px' }}>Navigation</h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            ×
          </button>
        </div>
        
        <nav>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {menuItems.map((item) => (
              <li key={item.id} style={{ marginBottom: '8px' }}>
                <NavLink
                  to={pagePathById[item.id] || '/dashboard'}
                  onClick={(event) => {
                    if (!item.active) {
                      event.preventDefault();
                      return;
                    }
                    handleItemClick(item.id, item.active);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: currentPage === item.id ? '#61CE70' : 'transparent',
                    color: item.active ? 'white' : '#888',
                    textAlign: 'left',
                    cursor: item.active ? 'pointer' : 'not-allowed',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    textDecoration: 'none',
                    pointerEvents: item.active ? 'auto' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (item.active && currentPage !== item.id) {
                      e.currentTarget.style.backgroundColor = '#333';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (item.active && currentPage !== item.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{item.icon}</span>
                  {item.label}
                  {!item.active && <span style={{ fontSize: '10px', marginLeft: 'auto' }}>🚧</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        
        <div style={{ 
          position: 'absolute', 
          bottom: '20px', 
          left: '20px', 
          right: '20px',
          borderTop: '1px solid #333',
          paddingTop: '15px'
        }}>
          <p style={{ 
            color: '#888', 
            fontSize: '12px', 
            textAlign: 'center',
            margin: 0
          }}>
            v1.0.0 - Phase 1
          </p>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
